/**
 * דיווחים חודשיים — the matrix board, and the screen the product exists for.
 *
 * Built to the hero mockup in landing/src/components/surfaces/ReportingBoard.astro:
 * period pill, KPI strip, tabbed filters, a dense table with per-authority dot notation
 * and a trailing status pill, and the footnote that explains the notation.
 *
 * The dot notation carries meaning, so it carries a legend — the seventh Ledger law. A
 * filled dot means the authority is relevant to this client this month; a hollow ring
 * means it is not, which is the difference between "nothing owed" and "nothing done".
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AUTHORITIES, formatDate, type AuthorityKey } from "@onecpa/shared";
import {
  Button,
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  IdentityCell,
  KpiStrip,
  PageHeader,
  RelevanceDot,
  Segmented,
  StatusBadge,
  TabStrip,
} from "@onecpa/ui";
import { api, type MatrixCell, type MatrixRow } from "../api/client.js";
import { usePeriod } from "../app/hooks.js";
import { useSession } from "../app/session.js";
import { AuthorityFileUpload } from "./AuthorityFileUpload.js";

type Filter = "all" | "awaiting" | "late" | "risk" | "done";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "הכל" },
  { id: "awaiting", label: "ממתין לחומר" },
  { id: "late", label: "באיחור" },
  { id: "risk", label: "סיכון לקנס" },
  { id: "done", label: "אושר" },
];

export function ReportingMatrix() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { period, label, next, previous, reset, isCurrent } = usePeriod();
  const [filter, setFilter] = useState<Filter>("all");
  const [density, setDensity] = useState<"comfortable" | "compact">("comfortable");
  const [uploadFor, setUploadFor] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["reporting", "matrix", session.orgId, period.year, period.month],
    queryFn: () => api.reporting.matrix(session.orgId, period.year, period.month),
  });

  const confirmOffice = useMutation({
    mutationFn: (id: string) => api.reporting.confirmOffice(session.orgId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["reporting"] });
      session.toast("סומן כאושר על ידי המשרד");
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const generate = useMutation({
    mutationFn: () => api.reporting.generate(session.orgId, period.year, period.month),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["reporting"] });
      session.toast(
        result.created > 0 ? `נוצרו ${result.created} דיווחים` : "כל הדיווחים לחודש כבר קיימים",
      );
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const authorities = (data?.authorities ?? []) as AuthorityKey[];

  const rows = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.rows;

    const matches = (cell: MatrixCell | undefined): boolean => {
      if (!cell) return false;
      switch (filter) {
        case "awaiting":
          return cell.status === "awaiting_client";
        case "late":
          return cell.status === "late";
        case "risk":
          return cell.status === "penalty_risk" || cell.status === "penalty_incurred";
        case "done":
          return cell.status === "office_confirmed" || cell.status === "client_confirmed";
        default:
          return true;
      }
    };

    return data.rows.filter((row) => Object.values(row.cells).some(matches));
  }, [data, filter]);

  // Deliberately not defaulted to `{}`: an absent summary has to stay absent so
  // the counts and KPIs can render as loading rather than as a confident zero.
  const summary = data?.summary;

  const columns: Column<MatrixRow>[] = [
    {
      key: "client",
      header: "לקוח",
      sticky: true,
      width: "220px",
      cell: (row) => (
        <IdentityCell
          name={row.clientName}
          detail={row.assignedStaffName ?? "לא מוקצה"}
          href={`/clients/${row.clientId}`}
        />
      ),
    },
    ...authorities.map<Column<MatrixRow>>((authority) => ({
      key: authority,
      header: AUTHORITIES[authority]?.short ?? authority,
      align: "center" as const,
      cell: (row) => {
        const cell = row.cells[authority];
        if (!cell) {
          return (
            <RelevanceDot
              relevant={false}
              label={`${AUTHORITIES[authority]?.label ?? authority}: לא רלוונטי החודש`}
            />
          );
        }

        const tone =
          cell.status === "penalty_risk" || cell.status === "penalty_incurred"
            ? "red"
            : cell.status === "late"
              ? "ochre"
              : cell.status === "office_confirmed" || cell.status === "client_confirmed"
                ? "ledger"
                : "pending";

        return (
          <RelevanceDot
            relevant
            tone={tone}
            label={`${AUTHORITIES[authority]?.label ?? authority}: ${cell.status}`}
          />
        );
      },
    })),
    {
      key: "due",
      header: "מועד",
      align: "center",
      hideOnSmall: true,
      cell: (row) => {
        const first = Object.values(row.cells).find(Boolean);
        return first ? (
          <bdi className="tnum text-xs text-ink-muted">{formatDate(first.dueDate)}</bdi>
        ) : null;
      },
    },
    {
      key: "status",
      header: "מצב",
      align: "end",
      cell: (row) => {
        const worst = worstCell(Object.values(row.cells).filter(Boolean) as MatrixCell[]);
        return worst ? <StatusBadge vocabulary="monthly" status={worst.status} /> : null;
      },
    },
    {
      key: "actions",
      header: "",
      align: "end",
      cell: (row) => {
        const pending = Object.values(row.cells).find(
          (cell) => cell && cell.status !== "office_confirmed",
        );
        return (
          <span className="flex items-center justify-end gap-1">
            {/* The upload belongs on the row rather than only on the authority
                screen: the matrix is where the office notices a filing is due,
                and making them navigate elsewhere to act on it is how a screen
                becomes a report nobody uses. */}
            <IconButton
              icon="upload"
              label={`העלאת קובץ לרשות — ${row.clientName}`}
              onClick={(event) => {
                event.stopPropagation();
                setUploadFor({ id: row.clientId, name: row.clientName });
              }}
            />
            {pending && (
              <IconButton
                icon="check"
                label={`אישור קבלת חומר — ${row.clientName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  confirmOffice.mutate(pending.id);
                }}
              />
            )}
          </span>
        );
      },
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  return (
    <>
      <PageHeader
        title="דיווחים חודשיים"
        period={
          <span className="inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={previous}
              aria-label="חודש קודם"
              className="px-1 hover:text-ink"
            >
              ‹
            </button>
            {label}
            <button
              type="button"
              onClick={next}
              aria-label="חודש הבא"
              className="px-1 hover:text-ink"
            >
              ›
            </button>
            {!isCurrent && (
              <button
                type="button"
                onClick={reset}
                className="ms-1 text-2xs text-ledger hover:underline"
              >
                חזרה לחודש הנוכחי
              </button>
            )}
          </span>
        }
        action={
          <Button
            variant="primary"
            icon="plus"
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            יצירת דיווחי החודש
          </Button>
        }
      >
        <TabStrip
          tabs={FILTERS.map((item) => ({
            id: item.id,
            label: item.label,
            count: summary
              ? item.id === "all"
                ? (summary.total ?? 0)
                : item.id === "awaiting"
                  ? (summary.awaiting_client ?? 0)
                  : item.id === "late"
                    ? (summary.late ?? 0)
                    : item.id === "risk"
                      ? (summary.penalty_risk ?? 0) + (summary.penalty_incurred ?? 0)
                      : (summary.office_confirmed ?? 0) + (summary.client_confirmed ?? 0)
              : undefined,
          }))}
          active={filter}
          onSelect={(id) => setFilter(id as Filter)}
        />
      </PageHeader>

      <KpiStrip
        items={[
          { label: "סה״כ דיווחים", value: summary?.total },
          { label: "ממתין לחומר", value: summary?.awaiting_client, tone: "pending" },
          { label: "אישר הלקוח", value: summary?.client_confirmed, tone: "ledger" },
          { label: "באיחור", value: summary?.late, tone: "ochre" },
          {
            label: "סיכון / קנס",
            value: summary ? (summary.penalty_risk ?? 0) + (summary.penalty_incurred ?? 0) : undefined,
            tone: "red",
          },
        ]}
      />

      <div className="flex items-center gap-3 px-5 pt-4">
        <Segmented<"comfortable" | "compact">
          options={[
            { value: "comfortable", label: "רגיל" },
            { value: "compact", label: "צפוף" },
          ]}
          value={density}
          onChange={setDensity}
        />
        <span className="text-2xs text-ink-faint">{rows.length} לקוחות מוצגים</span>
      </div>

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.clientId}
          onRowClick={(row) =>
            void navigate({ to: "/clients/$clientId", params: { clientId: row.clientId } })
          }
          loading={isLoading}
          density={density}
          caption="מצב הדיווחים לרשויות לפי לקוח ורשות"
          footnote={
            <>
              נקודה מלאה = הרשות רלוונטית ללקוח החודש · טבעת ריקה = אינה רלוונטית · ירוק = אושר ·
              חרדל = באיחור · אדום = סיכון לקנס או קנס בפועל
            </>
          }
          empty={
            <EmptyState
              title="אין דיווחים לחודש זה"
              description="ניתן ליצור את דיווחי החודש מתוך תדירויות הדיווח של הלקוחות."
              action={
                <Button
                  variant="primary"
                  onClick={() => generate.mutate()}
                  loading={generate.isPending}
                >
                  יצירת דיווחי החודש
                </Button>
              }
            />
          }
        />
      </div>

      <AuthorityFileUpload
        open={uploadFor !== null}
        onClose={() => setUploadFor(null)}
        clients={uploadFor ? [uploadFor] : []}
        clientId={uploadFor?.id}
      />
    </>
  );
}

/**
 * The row's headline status: the worst cell wins.
 *
 * A client with three confirmed filings and one penalty risk is a penalty risk. Showing
 * the majority state instead would bury exactly the row that needs attention.
 */
const SEVERITY: Record<string, number> = {
  penalty_incurred: 6,
  penalty_risk: 5,
  late: 4,
  awaiting_client: 3,
  client_confirmed: 2,
  office_confirmed: 1,
};

function worstCell(cells: MatrixCell[]): MatrixCell | null {
  return (
    cells.reduce<MatrixCell | null>((worst, cell) => {
      if (!worst) return cell;
      return (SEVERITY[cell.status] ?? 0) > (SEVERITY[worst.status] ?? 0) ? cell : worst;
    }, null) ?? null
  );
}

/** Used by the identity cell's link, which routes rather than reloading. */
export { Link };
